import { StyleGuide } from 'styles/StyleGuide';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface DrumVisualizationProps {
  levelPercent: number; // 0-100
  thresholdPercent?: number; // 0-100, optional threshold line
  currentLiters?: number; // Current volume in liters
  thresholdLiters?: number; // Threshold volume in liters
  maxCapacityLiters?: number; // Maximum capacity in liters, default 220
  height?: number; // Height in pixels, default 300
  width?: number; // Width in pixels, default 120
  className?: string;
  hideScales?: boolean;
}

export default function DrumVisualization({
  levelPercent,
  thresholdPercent,
  currentLiters,
  thresholdLiters,
  maxCapacityLiters = 220,
  height = 300,
  width = 170,
  className = '',
  hideScales = false,
}: DrumVisualizationProps) {
  const { t } = useTranslation();
  // Clamp values between 0 and 100
  const clampedLevel = Math.max(0, Math.min(100, levelPercent));
  const clampedThreshold = thresholdPercent
    ? Math.max(0, Math.min(100, thresholdPercent))
    : undefined;

  // Calculate fill height (from bottom)
  const fillHeight = (clampedLevel / 100) * (height - 20); // -20 for top/bottom margins
  const thresholdY = clampedThreshold
    ? height - 10 - (clampedThreshold / 100) * (height - 20)
    : undefined;

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div className="relative" style={{ width, height }}>
        <svg width={width} height={height} className="overflow-visible">
          {/* Drum outline */}
          <path
            d={`
              M 20 10
              L ${width - 20} 10
              Q ${width - 10} 10 ${width - 10} 20
              L ${width - 10} ${height - 20}
              Q ${width - 10} ${height - 10} ${width - 20} ${height - 10}
              L 20 ${height - 10}
              Q 10 ${height - 10} 10 ${height - 20}
              L 10 20
              Q 10 10 20 10
              Z
            `}
            fill="none"
            stroke="#374151"
            strokeWidth="2"
            className="drum-outline"
          />

          {/* Liquid fill */}
          {fillHeight > 0 && (
            <path
              d={`
                M 20 ${height - 10}
                L ${width - 20} ${height - 10}
                Q ${width - 10} ${height - 10} ${width - 10} ${height - 20}
                L ${width - 10} ${height - 10 - fillHeight}
                L 10 ${height - 10 - fillHeight}
                L 10 ${height - 20}
                Q 10 ${height - 10} 20 ${height - 10}
                Z
              `}
              fill={StyleGuide.palette.infoDark}
              fillOpacity="0.7"
              className="liquid-fill"
            />
          )}

          {/* Liquid surface (slight curve for realism) */}
          {fillHeight > 0 && (
            <ellipse
              cx={width / 2}
              cy={height - 10 - fillHeight}
              rx={(width - 20) / 2}
              ry="3"
              fill={StyleGuide.palette.infoDark}
              fillOpacity="0.8"
            />
          )}

          {/* Threshold line */}
          {thresholdY !== undefined && (
            <line
              x1="5"
              y1={thresholdY}
              x2={width - 5}
              y2={thresholdY}
              stroke="#EF4444"
              strokeWidth="2"
              strokeDasharray="5,5"
              className="threshold-line"
            />
          )}

          {/* Drum top ellipse */}
          <ellipse
            cx={width / 2}
            cy="10"
            rx={(width - 20) / 2}
            ry="5"
            fill="none"
            stroke="#374151"
            strokeWidth="2"
          />

          {/* Drum bottom ellipse */}
          <ellipse
            cx={width / 2}
            cy={height - 10}
            rx={(width - 20) / 2}
            ry="5"
            fill="none"
            stroke="#374151"
            strokeWidth="2"
          />
        </svg>

        {/* Percentage labels */}
        {!hideScales && (
          <div className="absolute -right-16 top-0 h-full flex flex-col justify-between text-xs text-gray-500">
            <div className="text-right">
              <div>{maxCapacityLiters}L</div>
              <div className="text-gray-400">100%</div>
            </div>
            <div className="text-right">
              <div>{Math.round(maxCapacityLiters * 0.75)}L</div>
              <div className="text-gray-400">75%</div>
            </div>
            <div className="text-right">
              <div>{Math.round(maxCapacityLiters * 0.5)}L</div>
              <div className="text-gray-400">50%</div>
            </div>
            <div className="text-right">
              <div>{Math.round(maxCapacityLiters * 0.25)}L</div>
              <div className="text-gray-400">25%</div>
            </div>
            <div className="text-right">
              <div>0L</div>
              <div className="text-gray-400">0%</div>
            </div>
          </div>
        )}

        {/* Current level indicator */}
        {!hideScales && (
          <div
            className="absolute -left-16 text-sm font-semibold text-blue-600"
            style={{ top: `${height - 10 - fillHeight - 10}px` }}
          >
            <div className="text-right">
              {currentLiters !== undefined && <div>{currentLiters.toFixed(0)}L</div>}
              <div className="text-xs font-normal">{clampedLevel.toFixed(0)}%</div>
            </div>
          </div>
        )}

        {/* Threshold indicator */}
        {thresholdY !== undefined && (
          <div
            className="absolute -right-6 text-xs font-medium text-red-500"
            style={{ top: `${thresholdY - 8}px` }}
          >
            <div className="text-right">
              {thresholdLiters !== undefined && (
                <div className="text-sm font-normal">{thresholdLiters.toFixed(0)}L</div>
              )}
              <div className="text-xs font-normal">{clampedThreshold?.toFixed(0)}%</div>
            </div>
          </div>
        )}

        {/* Volume indicator */}
        {/* <div className="absolute flex bottom-3 left-1/2 transform -translate-x-1/2 translate-y-8 text-xs text-gray-600 font-medium text-center">
          <div>
            {currentLiters !== undefined
              ? currentLiters.toFixed(0)
              : Math.round((clampedLevel / 100) * maxCapacityLiters)}
            L / {maxCapacityLiters}L
          </div>
          <div className="text-gray-400 mt-1">{clampedLevel.toFixed(0)}%</div>
        </div> */}
      </div>

      {/* Legend */}
      {!hideScales && (
        <div className="mt-4 flex items-center space-x-4 text-xs">
          <div className="flex items-center space-x-1">
            <div
              className="w-3 h-3 bg-opacity-70 rounded"
              style={{ backgroundColor: StyleGuide.palette.infoDark, opacity: 0.7 }}
            ></div>
            <span>{t('sensor.details.level.title')}</span>
          </div>
          {thresholdPercent !== undefined && (
            <div className="flex items-center space-x-1">
              <div className="w-3 h-1 bg-red-500" style={{ borderTop: '2px dashed #EF4444' }}></div>
              <span>{t('sensor.details.level.threshold')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
